import { useState, useEffect, useCallback, useRef } from 'react';
import { api, CertificationScore, ScoreItem } from '@/lib/api';
import { useScoreRefresh } from '@/contexts/ScoreRefreshContext';

export function useCertificationScores(
    certificationId: string,
    cluster: string,
    enNameMap: Map<string, { enCategory: string; enSubcategory: string }>
) {
    const [scores, setScores] = useState<Map<string, { score: number | null; reliability: number; raw_value: string | null }>>(new Map());
    const [allScores, setAllScores] = useState<Map<string, { score: number | null; reliability: number; raw_value: string | null }>>(new Map());
    const [saving, setSaving] = useState(false);
    const [loadingScores, setLoadingScores] = useState(true);

    // Context for refreshing other components
    const { refreshTrigger, triggerRefresh, weightProfileForClear } = useScoreRefresh();

    // Refs for stable callback access to prevent re-renders of memoized children
    const scoresRef = useRef(scores);
    const allScoresRef = useRef(allScores);
    useEffect(() => { scoresRef.current = scores; }, [scores]);
    useEffect(() => { allScoresRef.current = allScores; }, [allScores]);

    // Track loaded certification to avoid re-fetching scores unnecessarily
    // We also track refreshTrigger to force reload
    const loadedCertIdRef = useRef<string | null>(null);
    const lastRefreshTriggerRef = useRef<number>(-1);

    // Generate a unique key for a subcategory (always uses EN names for DB consistency)
    const getScoreKey = useCallback((categoryName: string, subcategoryName: string) => {
        // Look up EN names if we have a mapping
        const localeKey = `${categoryName}::${subcategoryName}`;
        const enNames = enNameMap.get(localeKey);
        if (enNames) {
            return `${enNames.enCategory}::${enNames.enSubcategory}`;
        }
        // Fallback to provided names (works for EN locale)
        return localeKey;
    }, [enNameMap]);

    // Load initial scores (when certification ID changes or refresh triggered)
    useEffect(() => {
        const loadScores = async () => {
            // Only fetch if certification ID changed OR refresh triggered
            if (loadedCertIdRef.current === certificationId && lastRefreshTriggerRef.current === refreshTrigger) {
                setLoadingScores(false);
                return;
            }

            setLoadingScores(true);
            try {
                const scoresResponse = await api.getCertificationScores(certificationId);
                const scoresMap = new Map<string, { score: number | null; reliability: number; raw_value: string | null }>();
                const allScoresMap = new Map<string, { score: number | null; reliability: number; raw_value: string | null }>();

                for (const s of scoresResponse.scores) {
                    const key = `${s.cluster}::${s.category_name}::${s.subcategory_name}`;
                    allScoresMap.set(key, {
                        score: s.score,
                        reliability: s.reliability,
                        raw_value: s.raw_value,
                    });
                    if (s.cluster === cluster) {
                        // Store using EN names as key (API returns EN names)
                        const enKey = `${s.category_name}::${s.subcategory_name}`;
                        scoresMap.set(enKey, {
                            score: s.score,
                            reliability: s.reliability,
                            raw_value: s.raw_value,
                        });
                    }
                }
                setScores(scoresMap);
                setAllScores(allScoresMap);
                // Set initial scores for change tracking
                initialScores.current = new Map(allScoresMap);
                loadedCertIdRef.current = certificationId;
                lastRefreshTriggerRef.current = refreshTrigger;
            } catch (error: any) {
                console.error('Failed to load scores:', error);
            } finally {
                setLoadingScores(false);
            }
        };

        loadScores();
    }, [certificationId, refreshTrigger]); // Dependent on certificationId and refreshTrigger

    // Rebuild cluster-specific scores when cluster changes (no re-fetch needed)
    useEffect(() => {
        // If we have allScores loaded, rebuild scores for the new cluster
        if (allScoresRef.current.size > 0) {
            const scoresMap = new Map<string, { score: number | null; reliability: number; raw_value: string | null }>();

            for (const [key, value] of allScoresRef.current.entries()) {
                // Key format: CLUSTER::category::subcategory
                const parts = key.split('::');
                if (parts.length >= 3 && parts[0] === cluster) {
                    const enKey = `${parts[1]}::${parts[2]}`;
                    scoresMap.set(enKey, value);
                }
            }

            setScores(scoresMap);
        }
    }, [cluster]);

    // Clear scores selectively when weightProfileForClear changes (e.g., when typology changes)
    useEffect(() => {
        if (!weightProfileForClear || !weightProfileForClear.weight_tree) return;

        console.log('Selectively clearing scores based on new weight profile');

        // Build a set of subcategories that have 0% weight in the new profile
        const zeroWeightKeys = new Set<string>();

        weightProfileForClear.weight_tree.forEach((clusterNode: any) => {
            // If cluster has 0 weight, all its children are disabled
            if (clusterNode.weighting === 0 || clusterNode.global_weight === 0) {
                clusterNode.children?.forEach((categoryNode: any) => {
                    categoryNode.children?.forEach((subcategoryNode: any) => {
                        const key = `${clusterNode.name_en}::${categoryNode.name_en}::${subcategoryNode.name_en}`;
                        zeroWeightKeys.add(key);
                    });
                });
            } else {
                // Check category level
                clusterNode.children?.forEach((categoryNode: any) => {
                    if (categoryNode.weighting === 0 || categoryNode.global_weight === 0) {
                        categoryNode.children?.forEach((subcategoryNode: any) => {
                            const key = `${clusterNode.name_en}::${categoryNode.name_en}::${subcategoryNode.name_en}`;
                            zeroWeightKeys.add(key);
                        });
                    } else {
                        // Check subcategory level
                        categoryNode.children?.forEach((subcategoryNode: any) => {
                            if (subcategoryNode.weighting === 0 || subcategoryNode.global_weight === 0) {
                                const key = `${clusterNode.name_en}::${categoryNode.name_en}::${subcategoryNode.name_en}`;
                                zeroWeightKeys.add(key);
                            }
                        });
                    }
                });
            }
        });

        console.log('Found', zeroWeightKeys.size, 'subcategories with 0% weight');

        // Clear scores for zero-weight subcategories
        setScores(prev => {
            const newScores = new Map(prev);
            for (const [key] of prev.entries()) {
                // Key format: category::subcategory
                // Need to check against all clusters since we don't know which cluster this belongs to
                let shouldClear = false;
                for (const fullKey of zeroWeightKeys) {
                    // fullKey format: CLUSTER::category::subcategory
                    const parts = fullKey.split('::');
                    if (parts.length >= 3) {
                        const catSubKey = `${parts[1]}::${parts[2]}`;
                        if (catSubKey === key && parts[0] === cluster) {
                            shouldClear = true;
                            break;
                        }
                    }
                }
                if (shouldClear) {
                    newScores.set(key, { score: null, reliability: 1.0, raw_value: null });
                }
            }
            return newScores;
        });

        setAllScores(prev => {
            const newAllScores = new Map(prev);
            for (const key of zeroWeightKeys) {
                if (prev.has(key)) {
                    newAllScores.set(key, { score: null, reliability: 1.0, raw_value: null });
                }
            }
            return newAllScores;
        });
    }, [weightProfileForClear, cluster]);



    // Track initial scores (from DB) vs changed scores
    const initialScores = useRef<Map<string, { score: number | null; reliability: number; raw_value: string | null }>>(new Map());
    const [changedScoreKeys, setChangedScoreKeys] = useState<Set<string>>(new Set());

    // Handle score change WITHOUT auto-save
    const handleScoreChange = useCallback(async (
        categoryName: string,
        subcategoryName: string,
        newScore: number | null | undefined,
        newReliability?: number,
        newRawValue?: string | null
    ) => {
        const key = getScoreKey(categoryName, subcategoryName);
        // Use REF for current state to avoid dependency on 'scores' state
        const currentScoresMap = scoresRef.current;
        const current = currentScoresMap.get(key) || { score: null, reliability: 1.0, raw_value: null };

        const updatedScore = {
            score: newScore !== undefined ? newScore : current.score,
            reliability: newReliability ?? current.reliability,
            raw_value: newRawValue !== undefined ? newRawValue : current.raw_value,
        };

        // Update local state immediately via functional update
        setScores(prev => {
            const newScores = new Map(prev);
            newScores.set(key, updatedScore);
            return newScores;
        });

        // Get EN Names for API call (ensures consistent DB keys)
        const localeKey = `${categoryName}::${subcategoryName}`;
        const enNames = enNameMap.get(localeKey);
        const apiCategoryName = enNames?.enCategory ?? categoryName;
        const apiSubcategoryName = enNames?.enSubcategory ?? subcategoryName;

        // Also update allScores for overall score calculation (use EN names)
        const allScoresKey = `${cluster}::${apiCategoryName}::${apiSubcategoryName}`;
        setAllScores(prev => {
            const newAllScores = new Map(prev);
            newAllScores.set(allScoresKey, updatedScore);
            return newAllScores;
        });

        // Mark this score as changed
        setChangedScoreKeys(prev => {
            const newSet = new Set(prev);
            newSet.add(allScoresKey); // Track using full key with cluster
            return newSet;
        });
    }, [cluster, getScoreKey, enNameMap]);

    // Manual save function for all changed scores
    const saveScores = useCallback(async () => {
        if (changedScoreKeys.size === 0) return;

        setSaving(true);
        try {
            const scoresToSave: ScoreItem[] = [];

            for (const fullKey of changedScoreKeys) {
                const parts = fullKey.split('::');
                if (parts.length >= 3) {
                    const scoreCluster = parts[0];
                    const categoryName = parts[1];
                    const subcategoryName = parts[2];
                    const scoreData = allScoresRef.current.get(fullKey);

                    if (scoreData) {
                        scoresToSave.push({
                            cluster: scoreCluster,
                            category_name: categoryName,
                            subcategory_name: subcategoryName,
                            score: scoreData.score,
                            reliability: scoreData.reliability,
                            raw_value: scoreData.raw_value,
                        });
                    }
                }
            }

            if (scoresToSave.length > 0) {
                await api.updateCertificationScores(certificationId, scoresToSave);
                // Clear changed keys after successful save
                setChangedScoreKeys(new Set());
                // Update initial scores to current state
                initialScores.current = new Map(allScoresRef.current);
                // Trigger refresh for other components (graphs, report)
                triggerRefresh();
            }
        } catch (error) {
            console.error('Failed to save scores:', error);
            throw error; // Re-throw so calling code can handle
        } finally {
            setSaving(false);
        }
    }, [certificationId, changedScoreKeys, triggerRefresh]);

    // Check if there are unsaved score changes
    const hasUnsavedScores = changedScoreKeys.size > 0;

    return {
        scores,
        allScores,
        saving,
        loadingScores,
        handleScoreChange,
        saveScores,
        hasUnsavedScores,
        getScoreKey
    };
}
